import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import { AppBarWithButtons } from '../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppUserAvatar from '../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppSwitchWithLabel from '../../../components/designe/app-switch-with-label/AppSwitchWithLabel';
import AppDashedBlockButton from '../../../components/designe/buttons/block-buttons/AppDashedBlockButton';
import AppFormDateTimePicker from '../../../components/designe/forms/app-form-date-time-picker/AppFormDateTimePicker';
import AppFormSelectWrapper from '../../../components/designe/forms/app-form-select-wrapper/AppFormSelectWrapper';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field/FormTextField';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import SavedProgramSetSuperset from '../../../components/reusable/saved-program-set-superset-modal/SavedProgramSetSuperset';
import SetGroupEditTable from '../../../components/reusable/set-group-edit-table/SetGroupEditTable';
import TrainingActivityTypePicker from '../../../components/reusable/training-activity-type-picker';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { AvatarSize, AvatarTypes } from '../../../shared/constants';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { IUser } from '../../../types/interfaces/user';
import { groupBy, orderBy } from '../../../utils/collections/array-utils';
import { getUserViewName } from '../../../utils/common/shared-util';
import { getSetKey } from '../../../utils/training/set-utils';
import useEditTable from '../edit-training/useEditTable';
import { useStyles } from './NewTraining.styles';
import { useNewTrainingPage } from './useNewTrainingPage';

function NewTraining() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { back } = useNavigationStack();

  const { classes } = useStyles(theme);

  const {
    formik,
    userProgramItems,
    canCreateForOthers,
    canChangeOwner,
    exerciseResults,
    user,
    performerSelectOptions,
    onCreateForOthersChange,
    isLoading,
    isSubmitDisabled,
    onAddEmptyTrainingSet,
    onProgramItemChange,
    defaultTrainingName,
    showSavedTrainingModal,
    setShowSavedTrainingModal,
    onAddTrainingSetFromSavedProgramSet,
  } = useNewTrainingPage();
  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction: back,
  });
  const { showUploadSetButton } = useUserSetPreferences();
  const trainingSetGroups = groupBy(
    orderBy(formik.values.trainingSets, 'setNumber'),
    'setNumber',
  );

  const useSetGroupEditTable = useEditTable(formik, {
    exerciseResults,
  });

  useScrollToFirstError(formik);

  return (
    <PageContainer title={t('trainings.newTraining')} backButton>
      <Box className={classes.addTrainingWrapper}>
        {dialog}
        <form
          onSubmit={formik.handleSubmit}
          className={classes.addTrainingForm}>
          <Box paddingTop={1} display="flex" flexDirection="column" gap={1}>
            <AppFormTextField
              label={t('common.title')}
              fullWidth
              autoFocus
              placeholder={defaultTrainingName}
              name="title"
              formik={formik}
            />
            <AppFormTextField
              label={t('common.description')}
              fullWidth
              multiline
              minRows={3}
              maxRows={15}
              name="description"
              formik={formik}
            />
            <AppFormDateTimePicker
              label={t('trainings.planedStartDate')}
              name="planedStartDate"
              formik={formik}
            />
            <TrainingActivityTypePicker formik={formik} fullWidth />

            <Box className={classes.createForOthersRow}>
              {canCreateForOthers ? (
                <>
                  <Box className={classes.createForOthersToggle}>
                    <AppSwitchWithLabel
                      label={t('trainings.createForOthers')}
                      labelPlacement="end"
                      onChange={onCreateForOthersChange}
                      value={!!formik.values.createForOthers}
                      disabled={!canChangeOwner}
                    />
                  </Box>

                  <Box className={classes.createForOthersSelectWrap}>
                    <AppFormSelectWrapper
                      label={t('trainings.trainingOwner')}
                      name="trainingOwner"
                      fieldName="displayName"
                      options={performerSelectOptions}
                      formik={formik}
                      sortBy="displayName"
                      fullWidth
                      disabled={
                        !canCreateForOthers ||
                        !formik.values.createForOthers ||
                        !canChangeOwner
                      }
                      disableClear={
                        !canCreateForOthers ||
                        !formik.values.createForOthers ||
                        !canChangeOwner
                      }
                      renderOption={(row: IUser & { displayName?: string }) => (
                        <Box display="flex" gap={1} alignItems="center">
                          <AppUserAvatar
                            entity={row}
                            size={AvatarSize.s}
                            type={AvatarTypes.round}
                          />
                          <Typography variant="text-md">
                            {row.displayName ?? getUserViewName(row)}
                          </Typography>
                        </Box>
                      )}
                      renderValue={(selected: IUser | null) => {
                        const entity = selected ?? user;
                        return entity ? (
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            sx={{ minWidth: 0 }}>
                            <AppUserAvatar
                              entity={entity}
                              size={AvatarSize.s}
                              type={AvatarTypes.round}
                            />
                            <Typography variant="text-md" noWrap>
                              {getUserViewName(entity)}
                            </Typography>
                          </Box>
                        ) : null;
                      }}
                    />
                  </Box>
                </>
              ) : null}
            </Box>

            <AppFormSelectWrapper
              data-onboarding="newTraining.programSelector"
              label={t('programs.programItem')}
              name="programItem"
              fieldName={'title'}
              options={userProgramItems}
              formik={formik}
              placeholder={t('programs.selectProgramItem')}
              onChange={(e) => onProgramItemChange(e)}></AppFormSelectWrapper>
          </Box>

          {formik.values.trainingSets &&
            formik.values.trainingSets.length > 0 &&
            Object.values(trainingSetGroups).map((trainingSetGroup) => (
              <Box marginTop={1} key={getSetKey(trainingSetGroup[0])}>
                <SetGroupEditTable
                  sets={trainingSetGroup}
                  useSetGroupEditTable={useSetGroupEditTable}
                />
              </Box>
            ))}

          <Box className={classes.addTrainingSetFooter}>
            <AppDashedBlockButton
              data-onboarding="newTraining.addTrainingSet"
              fullWidth
              onClick={() => onAddEmptyTrainingSet()}
              startIcon={<AddIcon />}
              yPadding={1}>
              {t('trainings.addTrainingSet')}
            </AppDashedBlockButton>
            {showUploadSetButton && (
              <AppDashedBlockButton
                fullWidth
                onClick={() => setShowSavedTrainingModal(true)}
                startIcon={<FileUploadIcon />}
                yPadding={1}>
                {t('trainings.addSavedSet')}
              </AppDashedBlockButton>
            )}
          </Box>
          <Box className={classes.addTrainingButtonWrapper}>
            <AppBarWithButtons
              loading={isLoading}
              disabled={isSubmitDisabled}
              onCancelClick={allowAndLeave}
              submitButtonProps={{ 'data-onboarding': 'newTraining.save' }}
            />
          </Box>
        </form>
      </Box>
      {showUploadSetButton && (
        <AppDialog
          open={!!showSavedTrainingModal}
          onClose={() => setShowSavedTrainingModal(false)}
          title={t('trainings.savedProgramSets')}>
          <SavedProgramSetSuperset
            onCancel={() => setShowSavedTrainingModal(false)}
            onSubmitSavedProgramSet={(value: {
              programSets: IProgramSet[];
              isSuperset: boolean;
            }) => {
              setShowSavedTrainingModal(false);
              onAddTrainingSetFromSavedProgramSet(
                value.programSets,
                value.isSuperset,
              );
            }}
          />
        </AppDialog>
      )}
    </PageContainer>
  );
}

export default withRouter(NewTraining);
